import { FormField } from '@/components/FormField';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Skeleton } from '@/components/ui/skeleton';
import { OptionSelect } from '@/components/OptionSelect';
import { CustomInput, SwitchRow } from './RunBuildControls';
import { autoSubmitDescription, guardrailsFor, showAutoSubmit } from './runBuildModel';
import type { RunBuildFormModel } from './useRunBuildForm';

/** The launch form's inputs (track, iOS distribution, generic properties, switches); skeleton while loading, problem when it failed. */
export function RunBuildInputs({ form }: { form: RunBuildFormModel }) {
  const { launch, launchData } = form.target;
  if (launch.isLoading && !launchData) return <Skeleton className="h-20 w-full" />;
  if (launchData) return <LaunchInputs form={form} />;
  return launch.isError ? <ProblemAlert error={launch.error} /> : null;
}

function LaunchInputs({ form }: { form: RunBuildFormModel }) {
  const { inputs } = form.fields.values;
  const { customInputs } = form.schema;
  const { setInputs } = form.fields.set;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <ChoiceInput form={form} name="track" label="Track" options={form.schema.tracks} visible />
      <ChoiceInput form={form} name="iosDistribution" label="iOS distribution" options={form.schema.iosDistributions} visible={form.target.selectedPlatforms.includes('ios')} />
      {customInputs.map(([key, prop]) => (
        <CustomInput key={key} name={key} prop={prop} value={inputs[key]} onChange={(v) => setInputs((s) => ({ ...s, [key]: v }))} hint={guardrailsFor(form.guardrails, key)[0]?.message} />
      ))}
      <LaunchSwitches form={form} />
    </div>
  );
}

/** A select over the launch form's allowed values (track, iOS distribution); hidden when there is nothing to choose. */
function ChoiceInput({ form, name, label, options, visible }: { form: RunBuildFormModel; name: 'track' | 'iosDistribution'; label: string; options: string[]; visible: boolean }) {
  if (options.length === 0 || !visible) return null;
  const id = `run-build-input-${name}`;
  const value = form.fields.values.inputs[name];
  return (
    <FormField id={id} label={form.schema.props[name]?.title ?? label} hint={guardrailsFor(form.guardrails, name)[0]?.message}>
      <OptionSelect
        id={id}
        data-testid={id}
        value={typeof value === 'string' ? value : ''}
        onValueChange={(v) => form.fields.set.setInputs((s) => ({ ...s, [name]: v }))}
        placeholder="Pick"
        options={options.map((t) => ({ value: t, label: t }))}
        className="font-mono sm:text-[13px]"
        contentClassName="font-mono"
      />
    </FormField>
  );
}

function LaunchSwitches({ form }: { form: RunBuildFormModel }) {
  const { inputs, autoSubmit } = form.fields.values;
  const { props } = form.schema;
  return (
    <div className="flex flex-col gap-3 sm:col-span-2">
      <SwitchRow id="run-build-input-clearCache" label={props.clearCache?.title ?? 'Clear build cache'} description="Re-install dependencies and pods from scratch" checked={inputs.clearCache === true} onChange={(v) => form.fields.set.setInputs((s) => ({ ...s, clearCache: v }))} />
      {showAutoSubmit(props, autoSubmit) ? (
        <SwitchRow id="run-build-autoSubmit" label={props.autoSubmit?.title ?? 'Submit after build'} description={autoSubmitDescription(inputs)} checked={autoSubmit === true} onChange={form.fields.set.setAutoSubmit} />
      ) : null}
    </div>
  );
}
